import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { Alert } from 'app/models/dto/alert.model';
import { Contact, NewContact } from 'app/models/dto/contact.model';
import { ContactRelationship } from 'app/models/dto/contact-relationship.model';
 import { ErcContact } from 'app/models/dto/erc-contact.model';
import { OwnerApplicationService } from 'app/modules/owner-application/models/owner-application.service';
import { ContactService } from 'app/services/contact/contact.service';
import { Observable, of } from 'rxjs';
import { catchError, tap, mergeMap } from 'rxjs/operators';
import { CreateNewBorrowerAlert } from '../borrower/alerts/borrower-alerts.actions';
import { ClearContactStore, CreateContactAlert, DismissContactAlert, GetContactsForBorrower, DeleteContact, UpdateContact, CreateContact, SetNewContact, UnsetNewContact, SetPrimaryContact, UnsetPrimaryContact, SendSignatureRequest, GetContactOwnerRelationships, GetContactEmployeeRelationships, GetErcContacts, CreateContactOwnerRelationship, UpdateErcContact, UpdateContactEmployeeRelationship, UpdateContactOwnerRelationship, DeleteContactRelationship, CreateContactEmployeeRelationship} from './contact.actions';
import { Injectable } from '@angular/core';
import { MessageService } from 'app/messages/message-service/message.service';
import { Message } from 'app/messages/message-service/message.model';

export class ContactStateModel {
	contacts: (Contact | NewContact)[];
  newContact: NewContact | null;
  loading: boolean;
  saving: {
    saving: boolean;
    contactId?: number | string;
  };
  signatureRequestSending: {
    sending: boolean;
    contactId?: number | string;
  };
  alerts: Alert[];
  contactOwnerRelationships: ContactRelationship[];
  contactEmployeeRelationships: ContactRelationship[];
  ercContacts: ErcContact[];
}

@State<ContactStateModel>({
	name: 'contact',
	defaults: {
		contacts: [],
    newContact: null,
    loading: false,
    saving: { saving: false },
    signatureRequestSending: { sending: false },
    alerts: [],
    contactOwnerRelationships: [],
    contactEmployeeRelationships: [],
    ercContacts: []
	}
})

@Injectable()

export class ContactState {

  @Selector()
	static contacts(state: ContactStateModel) {
    return state.contacts;
  }

  @Selector()
	static newContact(state: ContactStateModel) {
    return state.newContact;
  }

  @Selector()
	static primaryContact(state: ContactStateModel) {
    return state.contacts.find(c => c.isPrimary);
  }

  @Selector()
	static loading(state: ContactStateModel) {
    return state.loading;
  }

  @Selector()
	static saving(state: ContactStateModel) {
    return state.saving;
  }

  @Selector()
	static signatureRequestSending(state: ContactStateModel) {
    return state.signatureRequestSending;
  }

  @Selector()
	static alerts(state: ContactStateModel) {
    return state.alerts;
  }

  @Selector()
	static contactEmployeeRelationships(state: ContactStateModel) {
    return state.contactEmployeeRelationships;
  }

  @Selector()
	static contactOwnerRelationships(state: ContactStateModel) {
    return state.contactOwnerRelationships;
  }

  @Selector()
	static ercContacts(state: ContactStateModel) {
    return state.ercContacts;
  }

	constructor(
    private contactService: ContactService,
    private messageService: MessageService,
    private ownerApplicationService: OwnerApplicationService,
    private store: Store
  ) {}

	@Action(GetContactsForBorrower)
	getContactsForBorrower(
    { getState, patchState }: StateContext<ContactStateModel>,
    { borrowerId, refresh = false }: GetContactsForBorrower
  ) {
    const contactsInState = getState().contacts;
    if (
      contactsInState
      && contactsInState.constructor === Array
      && contactsInState.filter(c => c.borrowerId && c.borrowerId === borrowerId).length
      && !refresh
    ) {
      return;
    }
    if (getState().loading) { // if already loading contacts into state
      return;
    }
    patchState({ loading: true });
    return this.contactService.forBorrower(borrowerId).pipe(catchError(err => {
      this.store.dispatch(new CreateNewBorrowerAlert({
        level: 'error',
        message: 'There was an error fetching contacts for this borrower. Please refresh the page to try again.'
      }));
      patchState({ loading: false });
      return of();
    }), tap(contacts => {
      patchState({ contacts, loading: false });
    }));
	}

  @Action(GetContactOwnerRelationships)
  getContactOwnerRelationships(
    {getState, patchState}: StateContext<ContactStateModel>,
    {borrowerId}: GetContactOwnerRelationships
  ) {
    return this.contactService.forBorrowerContactOwnerRelationships(borrowerId).pipe(catchError(err=> {
      this.store.dispatch(new CreateNewBorrowerAlert({
        level: 'error',
        message: 'There was an error fetching contact owner relationships for this borrower. Please refresh the page to try again.'
      }));
      return of();
    }), tap(contactOwnerRelationships => {
      patchState({ contactOwnerRelationships});
    }));
  };

  @Action(GetContactEmployeeRelationships)
  getContactEmployeeRelationships(
    {getState, patchState}: StateContext<ContactStateModel>,
    {borrowerId}: GetContactEmployeeRelationships
  ) {
    return this.contactService.forBorrowerContactEmployeeRelationships(borrowerId).pipe(catchError(err=> {
      this.store.dispatch(new CreateNewBorrowerAlert({
        level: 'error',
        message: 'There was an error fetching contact employee relationships for this borrower. Please refresh the page to try again.'
      }));
      return of();
    }), tap(contactEmployeeRelationships => {
      patchState({ contactEmployeeRelationships });
    }));
  };

  @Action(GetErcContacts)
  getErcContacts(
    {getState, patchState}: StateContext<ContactStateModel>,
    {borrowerId}: GetErcContacts
  ) {
    return this.contactService.forBorrowerErcContacts(borrowerId).pipe(catchError(err=> {
      this.store.dispatch(new CreateNewBorrowerAlert({
        level: 'error',
        message: 'There was an error fetching contact erc contacts for this borrower. Please refresh the page to try again.'
      }));
      return of();
    }), tap(ercContacts => {
      patchState({ ercContacts: ercContacts.filter((ercContact => ercContact.contactId)) });
    }));
  };

  @Action(SetNewContact)
  SetNewContact({ patchState }: StateContext<ContactStateModel>, {}: SetNewContact) {
    const newContact = {
      id: null,
      first: null,
      last: null,
      percentOwnership: null,
      street: null,
      city: null,
      stateId: null,
      zipId: null,
      email: null
    };

    patchState({ newContact });
  }

  @Action(UnsetNewContact)
  UnsetNewContact({ patchState }: StateContext<ContactStateModel>, {}: UnsetNewContact) {
    patchState({ newContact: null });
  }

  @Action(CreateContact)
  createContact({ getState, patchState }: StateContext<ContactStateModel>, { newContact }: CreateContact) {
    if (this.percentageOver100(getState, newContact)) {
      return;
    }
    patchState({ saving: { saving: true, contactId: 'new' } });
    return this.contactService.create(newContact).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: err.message ? err.message : 'There was an error creating this contact. Please try again.'
      }));
    patchState({ saving: { saving: false } });
      return of();
    }), tap(contact => {
      patchState({ contacts: [ ...getState().contacts.filter(c => c.id), contact ], newContact: null, saving: { saving: false } });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: `Contact record for ${contact.first} created successfully.`
      }));
    }));
  }

  @Action(UpdateContact)
  updateContact({ getState, patchState }: StateContext<ContactStateModel>, { contact }: UpdateContact) {
    if (this.percentageOver100(getState, contact)) {
      return;
    }
    patchState({ saving: { saving: true, contactId: contact.id } });
    return this.contactService.update(contact).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: err.message ? err.message : 'There was an error updating this contact. Please try again.'
      }));
    patchState({ saving: { saving: false } });
      return of();
    }), tap(updatedContact => {
      const contactIndex = getState().contacts.findIndex(c => c.id === contact.id);
      const contactsCopy = [ ...getState().contacts ];
      contactsCopy[contactIndex] = updatedContact;
      patchState({ contacts: contactsCopy, saving: { saving: false } });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: `Contact record for ${updatedContact.first} updated successfully.`
      }));
    }));
  }

  @Action(UpdateErcContact)
  updateErcContact({getState, patchState}: StateContext<ContactStateModel>, { ercContact }: UpdateErcContact) {
    return this.contactService.updateErcContactOwnership(ercContact).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: `There was an error updating the contact ${ercContact.first} ${ercContact.last}. Please try again.`
      }));
      throw err;
    }), tap(() => {
      const ercContactsStateClone = [...getState().ercContacts];
      const updatedErcContacts = ercContactsStateClone.map((ercContactState) => {
        if(ercContactState.contactId === ercContact.contactId) {
          ercContactState.percentOwnership = ercContact.percentOwnership;
        }
        return ercContactState;
      });
      patchState({ ercContacts: updatedErcContacts });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: `Contact ${ercContact.first} ${ercContact.last} Updated successfully.`
      }));
    }));
  }

  @Action(UpdateContactEmployeeRelationship)
  updateContactEmployeeRelationship({getState, patchState}: 
    StateContext<ContactStateModel>, { contactEmployeeRelationship }: UpdateContactEmployeeRelationship) {

    return this.contactService.updateContactRelationship(contactEmployeeRelationship).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: 'There was an error updating the employee relationships. Please try again.'
      }));
      throw err;
    }), tap((result) => {
      const contactEmployeeRelationshipsStateClone = [...getState().contactEmployeeRelationships];
      const ercContacts = [...getState().ercContacts];
      const ercContact = ercContacts
      .find(ercContactItem => ercContactItem.contactId.toString() === result.ownerContactId.toString());
      if (ercContact && ercContact.first && ercContact.last) {
        result.first = ercContact.first;
        result.last = ercContact.last;
      }
      const updatedContactEmployeeRelationshipsState = contactEmployeeRelationshipsStateClone.map((contactEmployeeRelationshipState) => {
        if(contactEmployeeRelationshipState.id.toString() === contactEmployeeRelationship.id.toString()) {
          contactEmployeeRelationshipState = result;
        }
        return contactEmployeeRelationshipState;
      });
      patchState({ contactEmployeeRelationships: updatedContactEmployeeRelationshipsState });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: 'Employee Relationships Updated successfully.'
      }));
    }));
  }

  @Action(UpdateContactOwnerRelationship)
  updateContactOwnerRelationship({getState, patchState}: 
    StateContext<ContactStateModel>, { contactOwnerRelationship }: UpdateContactOwnerRelationship) {

    return this.contactService.updateContactRelationship(contactOwnerRelationship).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: 'There was an error updating the employee relationships. Please try again.'
      }));
      throw err;
    }), tap((result) => {
      const contactOwnerRelationshipsStateClone = [...getState().contactOwnerRelationships];
      const ercContacts = [...getState().ercContacts];

      const {owner, relatedTo} = ercContacts.reduce((ercContactsInformation: any, ercContactItem: ErcContact) => {
        if (ercContactItem.contactId.toString() === contactOwnerRelationship.ownerContactId.toString()) {
          ercContactsInformation['owner'] = ercContactItem;
        } else if (
            contactOwnerRelationship?.relatedToContactId &&
            ercContactItem.contactId.toString() === contactOwnerRelationship?.relatedToContactId.toString()) {
          ercContactsInformation['relatedTo'] = ercContactItem;
        }
        return ercContactsInformation;
      }, {});

      const updatedContactOwnerRelationshipsState = contactOwnerRelationshipsStateClone.map((contactOwnerRelationshipState) => {
        if(contactOwnerRelationshipState.id.toString() === contactOwnerRelationship.id.toString()) {
          contactOwnerRelationshipState = result;
          if (owner && owner.first && owner && relatedTo.first && relatedTo.last) {
            contactOwnerRelationshipState.first = owner.first;
            contactOwnerRelationshipState.last = owner.last;
            contactOwnerRelationshipState.relatedContactsFirst = relatedTo.first;
            contactOwnerRelationshipState.relatedContactsLast = relatedTo.last;
          }
        }
        return contactOwnerRelationshipState;
      });

      patchState({ contactOwnerRelationships: updatedContactOwnerRelationshipsState });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: 'Employee Relationships Updated successfully.'
      }));
    }));
  }

  @Action(CreateContactEmployeeRelationship)
  createContactEmployeeRelationship({getState, patchState}: 
    StateContext<ContactStateModel>, { contactEmployeeRelationship, borrowerId }: CreateContactEmployeeRelationship) {
    return this.contactService.createContactEmployeeRelationship(contactEmployeeRelationship, borrowerId).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: 'There was an error creating new employee relationship. Please try again.'
      }));
      throw err;
    }), tap((newContactEmployeeRelationship) => {
      const ercContacts = [...getState().ercContacts];
      const ercContact = ercContacts
      .find(ercContactItem => ercContactItem.contactId.toString() === newContactEmployeeRelationship.ownerContactId.toString());
      if (ercContact && ercContact.first && ercContact.last) {
        newContactEmployeeRelationship.first = ercContact.first;
        newContactEmployeeRelationship.last = ercContact.last;
      }
      const contactEmployeeRelationshipsStateClone = [...getState().contactEmployeeRelationships];

      contactEmployeeRelationshipsStateClone.push(newContactEmployeeRelationship);
      patchState({ contactEmployeeRelationships: contactEmployeeRelationshipsStateClone });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: 'Employee Relationships Updated successfully.'
      }));
    }));
  }

  @Action(CreateContactOwnerRelationship)
  createContactOwnerRelationship({getState, patchState}: 
    StateContext<ContactStateModel>, { contactOwnerRelationship, borrowerId }: CreateContactOwnerRelationship) {
    return this.contactService.createContactOwnerRelationship(contactOwnerRelationship, borrowerId).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: 'There was an error creating new employee relationship. Please try again.'
      }));
      throw err;
    }), tap((newContactOwnerRelationship) => {
      const ercContacts = [...getState().ercContacts];

      const {owner, relatedTo} = ercContacts.reduce((ercContactsInformation: any, ercContactItem: ErcContact) => {
        if (ercContactItem.contactId.toString() === newContactOwnerRelationship.ownerContactId.toString()) {
          ercContactsInformation['owner'] = ercContactItem;
        } else if (
            newContactOwnerRelationship?.relatedToContactId &&
            ercContactItem.contactId.toString() === newContactOwnerRelationship?.relatedToContactId.toString()) {
          ercContactsInformation['relatedTo'] = ercContactItem;
        }
        return ercContactsInformation;
      }, {});

      if (owner && owner.first && owner && relatedTo.first && relatedTo.last) {
        newContactOwnerRelationship.first = owner.first;
        newContactOwnerRelationship.last = owner.last;
        newContactOwnerRelationship.relatedContactsFirst = relatedTo.first;
        newContactOwnerRelationship.relatedContactsLast = relatedTo.last;
      }

      const contactOwnerRelationshipsStateClone = [...getState().contactOwnerRelationships];

      contactOwnerRelationshipsStateClone.push(newContactOwnerRelationship);
      patchState({ contactOwnerRelationships: contactOwnerRelationshipsStateClone });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: 'Employee Relationships Updated successfully.'
      }));
    }));
  }

  @Action(DeleteContact)
  deleteContact({ getState, patchState }: StateContext<ContactStateModel>, { contactId }: DeleteContact) {
    return this.contactService.delete(contactId).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: 'There was an error deleting this contact. Please try again.'
      }));
      throw err;
    }), tap(() => {
      patchState({ contacts: getState().contacts.filter(c => c.id !== contactId) });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: 'Contact deleted successfully.'
      }));
    }));
  }

  @Action(DeleteContactRelationship)
  deleteContactRelationship({getState, patchState}: 
    StateContext<ContactStateModel>, { contactRelationship, key }: DeleteContactRelationship) {
    return this.contactService.deleteContactRelationship(contactRelationship).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: 'There was an error deleting contact relationship. Please try again.'
      }));
      throw err;
    }), tap(() => {
      const contactRelationshipsStateClone = [...getState()[key]];
      let updatedContactRelationshipsState: any[] = [];
      if (contactRelationshipsStateClone.length > 1) {
        updatedContactRelationshipsState = contactRelationshipsStateClone
        .filter(ers => ers.id !== contactRelationship.id);
      }
      patchState({ [key]: [...updatedContactRelationshipsState] });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: 'Contact Relationship deleted successfully.'
      }));
    }));
  }

  @Action(UnsetPrimaryContact)
  unsetPrimaryContact({ getState, patchState }: StateContext<ContactStateModel>, { setPrimaryContact }: UnsetPrimaryContact) {
    const oldPrimaryContact: any =  getState().contacts.find(contact => (contact.isPrimary === 1 && contact.id !== null));
    if (oldPrimaryContact) {
      patchState({ saving: { saving: true, contactId: oldPrimaryContact.id} });
      oldPrimaryContact.isPrimary = null;
      return this.contactService.update(oldPrimaryContact).pipe(catchError(err => {
        this.store.dispatch(new CreateContactAlert({
          level: 'error',
          message: 'There was an error when un-setting the primary contact. Please try again.'
        }));
        patchState({ saving: { saving: false } });
        throw err;
      }), tap((updatedContact) => {
        const contactIndex = getState().contacts.findIndex(c => c.id === updatedContact.id);
        const contactsCopy = [ ...getState().contacts ];
        contactsCopy[contactIndex] = updatedContact;
        patchState({ contacts: contactsCopy, saving: { saving: false } });
        this.store.dispatch(new SetPrimaryContact(setPrimaryContact));
      }));
    }
  }

  @Action(SetPrimaryContact)
  setPrimaryContact({ getState, patchState }: StateContext<ContactStateModel>, { setPrimaryContact }: SetPrimaryContact) {
    setPrimaryContact.isPrimary = 1;
    patchState({ saving: { saving: true, contactId: setPrimaryContact.id} });
    return this.contactService.update(setPrimaryContact).pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: 'There was an error setting this contact as primary. Please try again.'
      }));
      patchState({ saving: { saving: false } });
      throw err;
    }), tap((updatedContact) => {
      const contactIndex = getState().contacts.findIndex(c => c.id === updatedContact.id);
      const contactsCopy = [ ...getState().contacts ];
      contactsCopy[contactIndex] = updatedContact;
      patchState({ contacts: contactsCopy, saving: { saving: false } });
      this.store.dispatch(new CreateContactAlert({
        level: 'success',
        message: 'Contact was successfully set as primary.'
      }));
    }));
  }

  @Action(SendSignatureRequest)
  sendSignatureRequest({ getState, patchState }: StateContext<ContactStateModel>, { contact }: SendSignatureRequest) {
    patchState({ signatureRequestSending: { sending: true, contactId: contact.id } });

    if (contact.isPrimary) {
      // primary owner needs to be sent an email to log in to merchant portal to sign
      return this.messageService.getTemplate('20564').pipe(
        catchError(err => {
          this.store.dispatch(new CreateContactAlert({
            level: 'error',
            message: err === 'EMAIL TEMPLATE NOT FOUND'
              ? 'The email template for this request (id 20564) could not be found in the database.'
              : 'There was an error sending the Marketplace Signature Request.'
          }));
          patchState({ signatureRequestSending: { sending: false } });
          throw err;
        }),
        mergeMap(template => {
          const message: Message = {
            ...template,
            html: template.content,
            read: null,
            borrowerId: contact.borrowerId,
            recipients: [contact.email],
            recipientCount: contact.email.length
          };
          return this.messageService.send(message).pipe(
            catchError(err => {
              this.store.dispatch(new CreateContactAlert({
                level: 'error',
                message: 'There was an error sending the Marketplace Signature Request.'
              }));
              patchState({ signatureRequestSending: { sending: false } });
              throw err;
            }),
            tap(response => {
              patchState({ signatureRequestSending: { sending: false } });
              this.store.dispatch(new CreateContactAlert({
                level: 'success',
                message: 'Marketplace signature request sent successfully.'
              }));
            }));
        }));
    } else {
    // non-primary owners need to be sent a docusign envelope to sign
    let signatureRequest$: Observable<any>;
    const envelope = this.ownerApplicationService.getMostRecentEnvelope(contact);
    if (envelope && envelope.signed === null && !envelope.deleted) {
      signatureRequest$ = this.ownerApplicationService.update(contact.id);
    } else {
      signatureRequest$ = this.ownerApplicationService.generateAndSend(contact.id);
    }
    return signatureRequest$.pipe(catchError(err => {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        message: err.code === 'EMAIL_IN_USE'
          ? 'That email is already in use by another owner. Please use a different email address.'
          : 'There was an error sending the Marketplace Signature Request.'
      }));
      patchState({ signatureRequestSending: { sending: false } });
      throw err;
    }), tap((updatedContact) => {
      if (contact.envelopes) {
        const contactIndex = getState().contacts.findIndex(c => c.id === updatedContact.id);
        if (contactIndex === -1) {
          return;
        }
        const contactToUpdate = { ...getState().contacts[contactIndex] };
        contactToUpdate.envelopes = updatedContact.envelopes;
        const contactsCopy = [ ...getState().contacts ];
        contactsCopy[contactIndex] = contactToUpdate;
        patchState({ contacts: contactsCopy, signatureRequestSending: { sending: false } });
        this.store.dispatch(new CreateContactAlert({
          level: 'success',
          message: 'Marketplace signature request sent successfully.'
        }));
      }
    }));
    }
  }

  @Action(CreateContactAlert)
  createContactAlert({ getState, patchState }: StateContext<ContactStateModel>, { alert }: CreateContactAlert) {
    alert.dismissDelay = 10000;
    patchState({ alerts: [ ...getState().alerts, alert ] });
  }

  @Action(DismissContactAlert)
  dismissContactAlert({ getState, patchState }: StateContext<ContactStateModel>, { alert }: DismissContactAlert) {
    patchState({ alerts: getState().alerts.filter(a => a.message !== alert.message) });
  }

  @Action(ClearContactStore)
  clearDealStore({ patchState }: StateContext<ContactStateModel>, { }: ClearContactStore) {
    patchState({ contacts: [] });
  }

  percentageOver100(getState, contact: any) {
    let percent = 0;
    getState().contacts.forEach(x =>
      ((x.id !== contact.id) && x.percentOwnership) ? percent += parseFloat(x.percentOwnership) : null);
    if (contact && contact.percentOwnership != null) {
        percent += parseFloat(contact.percentOwnership);
    }
    if (percent > 100) {
      this.store.dispatch(new CreateContactAlert({
        level: 'error',
        // eslint-disable-next-line max-len
        message: `This change would make the total ownership equal ${percent}%. Total ownership between listed business personnel must not exceed 100%.`
      }));
      return true;
    }
    return false;
  }
}
